// ProductosPorAlmacen.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, CircularProgress, Box,
  AppBar, Toolbar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip,
  Stack
} from '@mui/material';
import { styled } from '@mui/system';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 700,
  whiteSpace: 'nowrap'
}));

const esDeshabilitado = (p) => Boolean(p.isDisable ?? p.is_disabled);

function ProductosPorAlmacen() {
  const API = process.env.REACT_APP_API_URL || 'https://api.hmsaa.com.mx:5001';
  const API_URL = API.endsWith('/api') ? API : `${API}/api`;
  const JWT_TOKEN_KEY = 'access_token';

  const { almacenId } = useParams();
  const navigate = useNavigate();

  // ======================
  // ESTADO GENERAL
  // ======================
  const [productos, setProductos] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [nombreAlmacen, setNombreAlmacen] = useState('');
  const [loading, setLoading] = useState(true);

  // UI
  const [vista, setVista] = useState(''); // '' = con stock | residuos | deshabilitados | todos
  const [buscar, setBuscar] = useState('');

  // ======================
  // MOVIMIENTO
  // ======================
  const [modalOpen, setModalOpen] = useState(false);
  const [tipoMovimiento, setTipoMovimiento] = useState('');
  const [productoActivo, setProductoActivo] = useState(null);
  const [form, setForm] = useState({ cantidad: '', almacen_destino: '' });

  // ======================
  // HISTORIAL
  // ======================
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [filtros, setFiltros] = useState({ tipo: '', fecha_inicio: '', fecha_fin: '' });
  const [buscarHistorial, setBuscarHistorial] = useState('');

  // ======================
  // AUTH
  // ======================
  const token = localStorage.getItem(JWT_TOKEN_KEY);

  const esAdmin = (() => {
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return (
        payload.es_admin === true ||
        payload.es_admin === 1 ||
        payload.is_admin === true ||
        payload.is_admin === 1
      );
    } catch {
      return false;
    }
  })();

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }), [token]);

  // ======================
  // FETCHS
  // ======================
  const fetchProductos = useCallback(async () => {
    const res = await fetch(`${API_URL}/almacenes/${almacenId}/productos`);
    const data = await res.json();
    setProductos(Array.isArray(data) ? data : []);
  }, [API_URL, almacenId]);

  const fetchNombreAlmacen = useCallback(async () => {
    const res = await fetch(`${API_URL}/almacenes/${almacenId}`);
    const data = await res.json();
    setNombreAlmacen(data?.nombre_almacen || '');
  }, [API_URL, almacenId]);

  const fetchAlmacenes = useCallback(async () => {
    const res = await fetch(`${API_URL}/almacenes`);
    const data = await res.json();
    setAlmacenes(
      (Array.isArray(data) ? data : []).filter(
        a => a.id_almacen !== Number(almacenId)
      )
    );
  }, [API_URL, almacenId]);

  const fetchHistorial = useCallback(async () => {
    setLoadingHistorial(true);
    try {
      const params = new URLSearchParams();
      if (filtros.tipo) params.set('tipo', filtros.tipo);
      if (filtros.fecha_inicio) params.set('fecha_inicio', filtros.fecha_inicio);
      if (filtros.fecha_fin) params.set('fecha_fin', `${filtros.fecha_fin}T23:59:59`);
      const res = await fetch(
        `${API_URL}/almacenes/${almacenId}/historial?${params.toString()}`,
        { headers }
      );
      const data = await res.json();
      setHistorial(Array.isArray(data) ? data : []);
    } catch {
      Swal.fire('Error', 'No se pudo cargar el historial', 'error');
    } finally {
      setLoadingHistorial(false);
    }
  }, [API_URL, almacenId, filtros, headers]);

  // Carga el historial al abrirlo y cada vez que cambian sus filtros
  useEffect(() => {
    if (historialOpen) fetchHistorial();
  }, [historialOpen, fetchHistorial]);

  const historialFiltrado = useMemo(() => {
    const q = buscarHistorial.trim().toLowerCase();
    if (!q) return historial;
    return historial.filter((h) =>
      [h.producto, h.tipo, h.usuario, h.origen, h.destino, h.referencia]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [historial, buscarHistorial]);

  const limpiarHistorial = () => {
    setBuscarHistorial('');
    setFiltros({ tipo: '', fecha_inicio: '', fecha_fin: '' });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProductos(),
      fetchNombreAlmacen(),
      fetchAlmacenes()
    ]).finally(() => setLoading(false));
  }, [fetchProductos, fetchNombreAlmacen, fetchAlmacenes]);

  // ======================
  // DERIVADOS
  // ======================
  const conteos = useMemo(() => {
    let conStock = 0;
    let residuos = 0;
    let deshabilitados = 0;

    for (const p of productos) {
      if (Number(p.stock ?? 0) > 0) conStock += 1;
      else residuos += 1;
      if (esDeshabilitado(p)) deshabilitados += 1;
    }

    return { conStock, residuos, deshabilitados, todos: productos.length };
  }, [productos]);

  // Primero la vista elegida (con stock por defecto) y luego el buscador
  const productosVista = useMemo(() => {
    return productos.filter((p) => {
      const stock = Number(p.stock ?? 0);
      if (vista === 'residuos') return stock <= 0;
      if (vista === 'deshabilitados') return esDeshabilitado(p);
      if (vista === 'todos') return true;
      return stock > 0;
    });
  }, [productos, vista]);

  const productosFiltrados = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return productosVista;

    return productosVista.filter((p) => {
      const texto = [
        p.descripcion,
        p.marca,
        p.modelo,
        p.numero_serie
      ].filter(Boolean).join(' ').toLowerCase();
      return texto.includes(q);
    });
  }, [productosVista, buscar]);

  const limpiarFiltros = () => {
    setBuscar('');
    setVista('');
  };

  // ======================
  // MOVIMIENTOS
  // ======================
  const abrirMovimiento = (tipo, producto) => {
    if (!esAdmin) {
      Swal.fire('Acceso denegado', 'Solo administradores', 'warning');
      return;
    }
    setTipoMovimiento(tipo);
    setProductoActivo(producto);
    setForm({ cantidad: '', almacen_destino: '' });
    setModalOpen(true);
  };

  const enviarMovimiento = async () => {
    if (!productoActivo) return;

    let url = '';
    const payload = { id_producto: productoActivo.id_producto };

    if (tipoMovimiento === 'DESHABILITAR') {
      payload.id_almacen = Number(almacenId);
      url = '/almacen/baja';

      const confirm = await Swal.fire({
        title: '¿Deshabilitar producto?',
        text: productoActivo.descripcion,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, deshabilitar',
        cancelButtonText: 'Cancelar'
      });

      if (!confirm.isConfirmed) return;
    }

    if (tipoMovimiento === 'TRANSFERENCIA') {
      const cant = Number(form.cantidad);
      if (!cant || cant <= 0 || cant > Number(productoActivo.stock ?? 0)) {
        Swal.fire('Error', 'Cantidad inválida', 'error');
        return;
      }
      if (!form.almacen_destino) {
        Swal.fire('Error', 'Selecciona almacén destino', 'error');
        return;
      }
      payload.cantidad = cant;
      payload.almacen_origen = Number(almacenId);
      payload.almacen_destino = Number(form.almacen_destino);
      url = '/almacen/transferencia';
    }

    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    const data = await res.json();

    if (!res.ok) {
      Swal.fire('Error', data.error || 'Error en movimiento', 'error');
      return;
    }

    Swal.fire('Éxito', data.message || 'Movimiento registrado', 'success');
    setModalOpen(false);
    fetchProductos();
  };

  // ======================
  // UI HELPERS
  // ======================
  const renderEstadoChips = (p) => {
    const disabled = Boolean(p.isDisable ?? p.is_disabled);
    const stockNum = Number(p.stock ?? 0);
    const esResiduo = stockNum <= 0;

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {disabled && (
          <Chip size="small" label="DESHABILITADO" color="error" variant="outlined" />
        )}
        {esResiduo && (
          <Chip size="small" label="RESIDUO" variant="outlined" />
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
        <Typography sx={{ mt: 1, opacity: 0.75 }}>Cargando inventario…</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* HEADER */}
      <AppBar position="static">
        <Toolbar sx={{ gap: 1 }}>
          {/* <Button color="inherit" onClick={() => navigate('/almacenes')}>
            Volver
          </Button> */}

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
              Inventario
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {nombreAlmacen}
            </Typography>
          </Box>

          {esAdmin && (
            <Button
              onClick={() => {
                setHistorialOpen(true);
              }}
              variant="contained"
              disableElevation
              sx={{
                borderRadius: 999,
                px: 2.2,
                py: 0.9,
                textTransform: 'none',
                fontWeight: 800,
                letterSpacing: 0.2,
                boxShadow: 'none',
                backgroundColor: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.35)',
                backdropFilter: 'blur(6px)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.28)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 10px 22px rgba(0,0,0,0.22)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
                }
              }}
            >
              Historial
            </Button>
          )}

        </Toolbar>
      </AppBar>

      {/* FILTROS */}
      <BarraFiltros
        busqueda={buscar}
        onBusqueda={setBuscar}
        placeholder="Buscar por descripción, marca, modelo o serie"
        filtros={[
          {
            id: 'vista',
            tipo: 'chips',
            etiqueta: 'Ver',
            valor: vista,
            onChange: setVista,
            opciones: [
              { valor: '', etiqueta: `Con stock (${conteos.conStock})` },
              { valor: 'residuos', etiqueta: `Residuos (${conteos.residuos})` },
              { valor: 'deshabilitados', etiqueta: `Deshabilitados (${conteos.deshabilitados})` },
              { valor: 'todos', etiqueta: `Todos (${conteos.todos})` }
            ]
          }
        ]}
        total={productosVista.length}
        mostrados={productosFiltrados.length}
        onLimpiar={limpiarFiltros}
      />

      {/* TABLA */}
      <Box>
        <TableContainer component={Paper} sx={{ margin: '20px auto', maxWidth: '90%' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell>Producto</StyledTableCell>
                <StyledTableCell>Marca</StyledTableCell>
                <StyledTableCell>Modelo</StyledTableCell>
                <StyledTableCell>Serie</StyledTableCell>
                <StyledTableCell align="right">Stock</StyledTableCell>
                <StyledTableCell>Estado</StyledTableCell>
                <StyledTableCell align="center">Acciones</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {productosFiltrados.map((p) => {
                const disabled = Boolean(p.isDisable ?? p.is_disabled);
                const stockNum = Number(p.stock ?? 0);
                const esResiduo = stockNum <= 0;

                return (
                  <TableRow
                    key={p.id_producto}
                    hover={!esResiduo}
                    sx={{
                      opacity: disabled ? 0.5 : esResiduo ? 0.4 : 1,
                      backgroundColor: esResiduo ? 'rgba(0,0,0,0.02)' : 'inherit',
                      '& td': {
                        textDecoration: disabled ? 'line-through' : 'none',
                      }
                    }}
                  >
                    <TableCell sx={{ maxWidth: 520 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.descripcion}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{p.marca || '—'}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{p.modelo || '—'}</Typography>
                    </TableCell>

                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {p.numero_serie || '—'}
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700 }}
                      >
                        {stockNum}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {renderEstadoChips(p)}
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={!esAdmin || disabled || esResiduo}
                          onClick={() => abrirMovimiento('DESHABILITAR', p)}
                        >
                          Baja
                        </Button>

                        <Button
                          size="small"
                          color="secondary"
                          variant="contained"
                          disabled={!esAdmin || disabled || esResiduo}
                          onClick={() => abrirMovimiento('TRANSFERENCIA', p)}
                        >
                          Transferir
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {productosFiltrados.length === 0 && (
                <SinResultados
                  colSpan={7}
                  total={productos.length}
                  onLimpiar={limpiarFiltros}
                  entidad="productos"
                />
              )}
            </TableBody>

          </Table>
        </TableContainer>
      </Box>

      {/* MODAL MOVIMIENTO */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {tipoMovimiento === 'DESHABILITAR' ? 'Deshabilitar (Baja)' : 'Transferir'}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Producto: <b>{productoActivo?.descripcion}</b>
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Stock disponible: <b>{Number(productoActivo?.stock ?? 0)}</b>
          </Typography>

          {tipoMovimiento === 'TRANSFERENCIA' && (
            <>
              <TextField
                label="Cantidad"
                type="number"
                fullWidth
                sx={{ mt: 2 }}
                value={form.cantidad}
                onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
              />

              <TextField
                select
                label="Almacén destino"
                fullWidth
                sx={{ mt: 2 }}
                value={form.almacen_destino}
                onChange={(e) => setForm({ ...form, almacen_destino: e.target.value })}
              >
                {almacenes.map(a => (
                  <MenuItem key={a.id_almacen} value={a.id_almacen}>
                    {a.nombre_almacen}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {tipoMovimiento === 'DESHABILITAR' && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Esto deshabilita el producto (no descuenta stock) y queda registrado en el historial.
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={enviarMovimiento}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL HISTORIAL */}
      <Dialog open={historialOpen} onClose={() => setHistorialOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Historial de movimientos</DialogTitle>

        <BarraFiltros
          busqueda={buscarHistorial}
          onBusqueda={setBuscarHistorial}
          placeholder="Buscar por producto, usuario o referencia"
          filtros={[
            {
              id: 'tipo',
              tipo: 'select',
              etiqueta: 'Tipo',
              textoTodos: 'Todos los tipos',
              valor: filtros.tipo,
              onChange: (v) => setFiltros((f) => ({ ...f, tipo: v })),
              opciones: [
                { valor: 'ENTRADA', etiqueta: 'Entrada' },
                { valor: 'SALIDA', etiqueta: 'Salida' },
                { valor: 'TRANSFERENCIA', etiqueta: 'Transferencia' },
                { valor: 'AJUSTE', etiqueta: 'Ajuste / baja' }
              ]
            },
            {
              id: 'desde',
              tipo: 'fecha',
              etiqueta: 'Desde',
              valor: filtros.fecha_inicio,
              onChange: (v) => setFiltros((f) => ({ ...f, fecha_inicio: v }))
            },
            {
              id: 'hasta',
              tipo: 'fecha',
              etiqueta: 'Hasta',
              valor: filtros.fecha_fin,
              onChange: (v) => setFiltros((f) => ({ ...f, fecha_fin: v }))
            }
          ]}
          total={historial.length}
          mostrados={historialFiltrado.length}
          onLimpiar={limpiarHistorial}
        />

        <DialogContent dividers>
          {loadingHistorial ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Cantidad</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>Destino</TableCell>
                  <TableCell>Referencia</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {historialFiltrado.map((h, idx) => (
                  <TableRow key={`${h.fecha}-${h.tipo}-${h.producto}-${h.usuario}-${idx}`}>
                    <TableCell>{h.fecha}</TableCell>
                    <TableCell>{h.producto}</TableCell>
                    <TableCell>{h.tipo}</TableCell>
                    <TableCell>{h.cantidad}</TableCell>
                    <TableCell>{h.usuario}</TableCell>
                    <TableCell>{h.origen || '-'}</TableCell>
                    <TableCell>{h.destino || '-'}</TableCell>
                    <TableCell>{h.referencia || '-'}</TableCell>
                  </TableRow>
                ))}

                {historialFiltrado.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography sx={{ opacity: 0.7 }}>
                        No hay movimientos para mostrar.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setHistorialOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProductosPorAlmacen;
